import React, { useState, useEffect, useRef } from 'react';
import { Message, User } from './types';
import { queryProcedures, healthCheck } from './api';
import ChatMessage from './components/ChatMessage';
import ChatInput from './components/ChatInput';
import UserHeader from './components/UserHeader';
import { Trash2, Wifi, WifiOff } from 'lucide-react';


const App: React.FC = () => {
  const [messages, setMessages] = useState<Message[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isConnected, setIsConnected] = useState<boolean | null>(null);
  const [user, setUser] = useState<User | null>(null);
  const [isAuthenticated, setIsAuthenticated] = useState<boolean | null>(null);
  const [conversationId, setConversationId] = useState<string | null>(null);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  // Holds the controller for the in-flight query so the Stop button can abort it.
  const abortControllerRef = useRef<AbortController | null>(null);

  // Check authentication and API connection on mount
  useEffect(() => {
    const checkAuthentication = async () => {
      // Check if user data is in URL params (from OAuth callback)
      const urlParams = new URLSearchParams(window.location.search);
      const userParam = urlParams.get('user');
      
      if (userParam) {
        try {
          const userData = JSON.parse(decodeURIComponent(userParam));
          setUser(userData);
          setIsAuthenticated(true);
          // Clean up URL
          window.history.replaceState({}, document.title, window.location.pathname);
        } catch (error) {
          console.error('Error parsing user data:', error);
          setIsAuthenticated(false);
        }
      } else {
        // No user data in URL, check if we have stored user data
        const storedUser = localStorage.getItem('user');
        if (storedUser) {
          try {
            const userData = JSON.parse(storedUser);
            setUser(userData);
            setIsAuthenticated(true);
          } catch (error) {
            console.error('Error parsing stored user data:', error);
            localStorage.removeItem('user');
            setIsAuthenticated(false);
          }
        } else {
          setIsAuthenticated(false);
        }
      }

      // Check API connection
      const connected = await healthCheck();
      setIsConnected(connected);
    };

    checkAuthentication();
  }, []);

  // Redirect to login if not authenticated
  useEffect(() => {
    if (isAuthenticated === false) {
      window.location.href = '/';
    }
  }, [isAuthenticated]);

  // Store user data in localStorage when user is set
  useEffect(() => {
    if (user) {
      localStorage.setItem('user', JSON.stringify(user));
    }
  }, [user]);

  // Auto-scroll to bottom when new messages arrive
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const handleSendMessage = async (content: string) => {
    // Add user message
    const userMessage: Message = {
      id: Date.now().toString(),
      type: 'user',
      content,
      timestamp: new Date(),
    };
    setMessages(prev => [...prev, userMessage]);
    setIsLoading(true);

    // New controller for this request so Stop can abort just this one.
    const controller = new AbortController();
    abortControllerRef.current = controller;

    try {
      // Query the API with conversation_id to maintain state
      const response = await queryProcedures({
        message: content,
        conversation_id: conversationId || undefined,
        user_id: user?.id || undefined,
        user_email: user?.email || undefined  // For form access control by email
      }, controller.signal);

      // Store conversation_id for next request
      if (response.conversation_id) {
        setConversationId(response.conversation_id);
      }

      // Add assistant message
      const assistantMessage: Message = {
        id: (Date.now() + 1).toString(),
        type: 'assistant',
        content: response.response,
        timestamp: new Date(),
        sources: response.sources,
      };
      setMessages(prev => [...prev, assistantMessage]);
    } catch (error) {
      if ((error as any)?.name === 'AbortError') {
        // User cancelled: show a subtle notice instead of an error.
        const cancelledMessage: Message = {
          id: (Date.now() + 1).toString(),
          type: 'assistant',
          content: 'Request cancelled.',
          timestamp: new Date(),
        };
        setMessages(prev => [...prev, cancelledMessage]);
      } else {
        // Add error message
        const errorMessage: Message = {
          id: (Date.now() + 1).toString(),
          type: 'assistant',
          content: `Sorry, I encountered an error: ${error instanceof Error ? error.message : 'Unknown error'}`,
          timestamp: new Date(),
        };
        setMessages(prev => [...prev, errorMessage]);
      }
    } finally {
      abortControllerRef.current = null;
      setIsLoading(false);
    }
  };

  const handleCancelRequest = () => {
    abortControllerRef.current?.abort();
  };

  const handleSignOut = () => {
    setUser(null);
    setMessages([]);
    setIsAuthenticated(false);
    // Clear stored user data
    localStorage.removeItem('user');
    // Redirect to login page
    window.location.href = '/';
  };

  const handleClearChat = () => {
    if (window.confirm('Are you sure you want to clear all messages?')) {
      setMessages([]);
      setConversationId(null); // Reset conversation ID when clearing chat
    }
  };

  // Group messages by date
  const groupMessagesByDate = (messages: Message[]) => {
    const groups: { date: string; messages: Message[] }[] = [];
    const today = new Date();
    const yesterday = new Date(today);
    yesterday.setDate(yesterday.getDate() - 1);

    messages.forEach((message) => {
      const messageDate = new Date(message.timestamp);
      let dateLabel = '';

      if (messageDate.toDateString() === today.toDateString()) {
        dateLabel = 'Today';
      } else if (messageDate.toDateString() === yesterday.toDateString()) {
        dateLabel = 'Yesterday';
      } else {
        dateLabel = messageDate.toLocaleDateString();
      }

      const existingGroup = groups.find(group => group.date === dateLabel);
      if (existingGroup) {
        existingGroup.messages.push(message);
      } else {
        groups.push({ date: dateLabel, messages: [message] });
      }
    });

    return groups;
  };

  // Show loading while checking authentication
  if (isAuthenticated === null) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-50 via-blue-50 to-indigo-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600 mx-auto mb-4"></div>
          <p className="text-secondary-600">Checking authentication...</p>
        </div>
      </div>
    );
  }

  // Don't render anything if not authenticated (will redirect)
  if (isAuthenticated === false) {
    return null;
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 via-blue-50 to-indigo-50 flex flex-col relative">
      {/* Background Logo with Transparency */}
      <div className="absolute inset-0 pointer-events-none opacity-5 flex items-center justify-center p-4 z-0">
        <img 
          src="/logo_vertical.png" 
          alt="Background Logo" 
          className="max-w-full max-h-full w-auto h-auto object-contain"
        />
      </div>
      
      {/* Header */}
      <header className="glass border-b border-white/20 px-4 sm:px-6 py-3 sm:py-4 shadow-soft relative z-20">
        <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
              <div className="relative">
                <img src="/chat_logo.png" alt="Chat Icon" className="w-8 h-8 sm:w-9 sm:h-9 object-contain animate-bounce-in" />
                <div className="absolute -top-1 -right-1 w-3 h-3 bg-success-500 rounded-full animate-pulse-slow"></div>
              </div>
              <h1 className="text-xl sm:text-2xl font-bold bg-gradient-to-r from-primary-600 to-primary-800 bg-clip-text text-transparent">
                Procedures Chatbot
              </h1>
            </div>
          
                                  <div className="flex items-center gap-3">
              {/* Clear Chat Button */}
              {messages.length > 0 && (
                <button
                  onClick={handleClearChat}
                  className="p-2 text-secondary-500 hover:text-error-500 hover:bg-error-50 rounded-lg transition-all duration-200 transform hover:scale-105 focus-ring"
                  title="Clear chat"
                >
                  <Trash2 className="w-4 h-4" />
                </button>
              )}
              
              {/* User Header - Right Side */}
              <UserHeader user={user} onSignOut={handleSignOut} />
            </div>
        </div>
      </header>

      {/* Chat Messages */}
      <div className="flex-1 overflow-y-auto p-3 sm:p-6 relative z-10">
        {messages.length === 0 ? (
          // Welcome Message
          <div className="flex flex-col items-center justify-center h-full text-center max-w-2xl mx-auto">
            <div className="neumorphism p-8 rounded-3xl animate-fade-in">
              <div className="relative mb-8">
                <img src="/chat_logo.png" alt="Chat Bot" className="w-20 h-20 mx-auto mb-6 animate-bounce-in" />
                <div className="absolute -top-2 -right-2 w-6 h-6 bg-success-500 rounded-full animate-pulse-slow"></div>
              </div>
              <h2 className="text-3xl font-bold bg-gradient-to-r from-primary-600 to-primary-800 bg-clip-text text-transparent mb-4">
                Welcome to Procedures Chatbot
              </h2>
              <p className="text-secondary-600 mb-6 text-lg leading-relaxed">
                Ask me anything about your company procedures and I'll help you find the information you need.
              </p>
              <div className="text-center">
                <h3 className="text-2xl font-bold text-secondary-800 mb-3">مرحبًا بك في روبوت إجراءات الشركة.</h3>
                <p className="text-secondary-600 text-lg leading-relaxed">
                  اسألني عن أي شيء يخص إجراءات شركتك، وسأساعدك في العثور على المعلومات التي تحتاجها.
                </p>
              </div>
            </div>
          </div>
        ) : (
          <div className="space-y-4">
            {groupMessagesByDate(messages).map((group) => (
              <div key={group.date}>
                {/* Date Separator */}
                <div className="flex items-center justify-center my-6">
                  <div className="bg-gray-100 px-3 py-1 rounded-full text-xs text-gray-500 font-medium">
                    {group.date}
                  </div>
                </div>
                {/* Messages for this date */}
                <div className="space-y-4">
                  {group.messages.map((message) => (
                    <ChatMessage key={message.id} message={message} user={user} />
                  ))}
                </div>
              </div>
            ))}
            {isLoading && (
              <div className="flex justify-start mb-4 animate-slide-up">
                <div className="flex items-start gap-3">
                  <div className="w-10 h-10 bg-gradient-to-br from-primary-100 to-primary-200 rounded-full flex items-center justify-center shadow-soft">
                    <img src="/chat_logo.png" alt="Chat Bot" className="w-6 h-6 object-contain animate-typing" />
                  </div>
                  <div className="glass rounded-2xl p-4 shadow-soft">
                    <div className="loading-dots text-primary-500">
                      <span></span>
                      <span></span>
                      <span></span>
                    </div>
                    <p className="text-xs text-secondary-500 mt-2">Thinking...</p>
                  </div>
                </div>
              </div>
            )}
          </div>
        )}
        <div ref={messagesEndRef} />
      </div>

      {/* Chat Input */}
      <ChatInput
        onSendMessage={handleSendMessage}
        onCancel={handleCancelRequest}
        isLoading={isLoading}
        disabled={!isConnected}
      />
    </div>
  );
};

export default App;
